import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

// ---------------------------------------------------------------------------
// Internal mathematical helpers
// ---------------------------------------------------------------------------

/** Log-gamma using Lanczos approximation (accurate to ~15 digits) */
function gammaLn(x: number): number {
    if (x <= 0) return Infinity;
    if (x < 0.5) {
        return Math.log(Math.PI / Math.sin(Math.PI * x)) - gammaLn(1 - x);
    }
    const g = 7;
    const c = [
        0.99999999999980993,
        676.5203681218851,
        -1259.1392167224028,
        771.32342877765313,
        -176.61502916214059,
        12.507343278686905,
        -0.13857109526572012,
        9.9843695780195716e-6,
        1.5056327351493116e-7,
    ];
    const xm = x - 1;
    const tmp = xm + g + 0.5;
    let ser = c[0];
    for (let i = 1; i < g + 2; i++) {
        ser += c[i] / (xm + i);
    }
    return 0.5 * Math.log(2 * Math.PI) + (xm + 0.5) * Math.log(tmp) - tmp + Math.log(ser);
}

/** Gamma function via exp(gammaLn(x)) */
function gammaFn(x: number): number {
    return Math.exp(gammaLn(x));
}

/** Regularized lower incomplete gamma P(a, x) using series expansion */
function gammIncP(a: number, x: number): number {
    if (x < 0) return 0;
    if (x === 0) return 0;
    if (a <= 0) return NaN;
    // Use series for small x, continued fraction for large x
    if (x < a + 1) {
        // Series
        let ap = a;
        let sum = 1 / a;
        let del = sum;
        const eps = 3e-14;
        for (let n = 0; n < 300; n++) {
            ap++;
            del *= x / ap;
            sum += del;
            if (Math.abs(del) < Math.abs(sum) * eps) break;
        }
        return sum * Math.exp(-x + a * Math.log(x) - gammaLn(a));
    } else {
        return 1 - gammIncQ(a, x);
    }
}

/** Regularized upper incomplete gamma Q(a, x) via continued fraction */
function gammIncQ(a: number, x: number): number {
    if (x < 0) return 1;
    if (x === 0) return 1;
    if (a <= 0) return NaN;
    if (x < a + 1) {
        return 1 - gammIncP(a, x);
    }
    // Lentz continued fraction
    const eps = 3e-14;
    const fpmin = 1e-300;
    let b = x + 1 - a;
    let c = 1 / fpmin;
    let d = 1 / b;
    let h = d;
    for (let i = 1; i <= 300; i++) {
        const an = -i * (i - a);
        b += 2;
        d = an * d + b;
        if (Math.abs(d) < fpmin) d = fpmin;
        c = b + an / c;
        if (Math.abs(c) < fpmin) c = fpmin;
        d = 1 / d;
        const del = d * c;
        h *= del;
        if (Math.abs(del - 1) < eps) break;
    }
    return Math.exp(-x + a * Math.log(x) - gammaLn(a)) * h;
}

/** Regularized incomplete beta function I_x(a,b) via continued fraction (Lentz) */
function betaInc(x: number, a: number, b: number): number {
    if (x < 0 || x > 1) return NaN;
    if (x === 0) return 0;
    if (x === 1) return 1;
    const lbeta = gammaLn(a) + gammaLn(b) - gammaLn(a + b);
    const front = Math.exp(Math.log(x) * a + Math.log(1 - x) * b - lbeta) / a;

    // Use symmetry relation to improve convergence
    if (x > (a + 1) / (a + b + 2)) {
        return 1 - betaInc(1 - x, b, a);
    }

    // Continued fraction via Lentz
    const eps = 3e-14;
    const fpmin = 1e-300;
    const qab = a + b;
    const qap = a + 1;
    const qam = a - 1;
    let c = 1;
    let d = 1 - qab * x / qap;
    if (Math.abs(d) < fpmin) d = fpmin;
    d = 1 / d;
    let h = d;
    for (let m = 1; m <= 300; m++) {
        const m2 = 2 * m;
        let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
        d = 1 + aa * d;
        if (Math.abs(d) < fpmin) d = fpmin;
        c = 1 + aa / c;
        if (Math.abs(c) < fpmin) c = fpmin;
        d = 1 / d;
        h *= d * c;
        aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
        d = 1 + aa * d;
        if (Math.abs(d) < fpmin) d = fpmin;
        c = 1 + aa / c;
        if (Math.abs(c) < fpmin) c = fpmin;
        d = 1 / d;
        const del = d * c;
        h *= del;
        if (Math.abs(del - 1) < eps) break;
    }
    return front * h;
}

/** Inverse regularized incomplete beta via bisection + Newton-Raphson */
function betaIncInv(p: number, a: number, b: number): number {
    if (p <= 0) return 0;
    if (p >= 1) return 1;
    // Initial guess
    let x = a / (a + b);
    // Bisection refinement
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 200; i++) {
        const fx = betaInc(x, a, b) - p;
        if (Math.abs(fx) < 1e-12) break;
        if (fx < 0) lo = x;
        else hi = x;
        x = (lo + hi) / 2;
    }
    return x;
}

/** Standard normal CDF using rational approximation (Abramowitz & Stegun) */
function normalCDF(x: number): number {
    if (x === 0) return 0.5;
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const poly = t * (0.319381530 +
        t * (-0.356563782 +
            t * (1.781477937 +
                t * (-1.821255978 +
                    t * 1.330274429))));
    const pdf = Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
    const result = 1 - pdf * poly;
    return x >= 0 ? result : 1 - result;
}

/** Inverse standard normal CDF - rational approximation (Peter Acklam) */
function normalInvCDF(p: number): number {
    if (p <= 0) return -Infinity;
    if (p >= 1) return Infinity;
    if (p === 0.5) return 0;

    const a = [-3.969683028665376e+01, 2.209460984245205e+02,
        -2.759285104469687e+02, 1.383577518672690e+02,
        -3.066479806614716e+01, 2.506628277459239e+00];
    const b = [-5.447609879822406e+01, 1.615858368580409e+02,
        -1.556989798598866e+02, 6.680131188771972e+01,
        -1.328068155288572e+01];
    const c = [-7.784894002430293e-03, -3.223964580411365e-01,
        -2.400758277161838e+00, -2.549732539343734e+00,
        4.374664141464968e+00, 2.938163982698783e+00];
    const d = [7.784695709041462e-03, 3.224671290700398e-01,
        2.445134137142996e+00, 3.754408661907416e+00];

    const pLow = 0.02425;
    const pHigh = 1 - pLow;
    let q: number;
    let r: number;

    if (p < pLow) {
        q = Math.sqrt(-2 * Math.log(p));
        return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
            ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    } else if (p <= pHigh) {
        q = p - 0.5;
        r = q * q;
        return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
            (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
    } else {
        q = Math.sqrt(-2 * Math.log(1 - p));
        return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
            ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    }
}

/** Student's t CDF P(T <= x) with df degrees of freedom */
function tCDF(x: number, df: number): number {
    if (!isFinite(x)) return x > 0 ? 1 : 0;
    const ibeta = betaInc(df / (df + x * x), df / 2, 0.5);
    const p = 0.5 * ibeta;
    return x >= 0 ? 1 - p : p;
}

/** Inverse t CDF (two-tailed area p) */
function tInvCDF(p: number, df: number): number {
    // p is the cumulative probability
    if (p <= 0 || p >= 1 || df <= 0) return NaN;
    if (p === 0.5) return 0;
    // Bisection
    let lo = -1000;
    let hi = 1000;
    for (let i = 0; i < 300; i++) {
        const mid = (lo + hi) / 2;
        const fmid = tCDF(mid, df) - p;
        if (Math.abs(fmid) < 1e-12) return mid;
        if (fmid < 0) lo = mid;
        else hi = mid;
    }
    return (lo + hi) / 2;
}

/** Chi-squared CDF via regularized lower incomplete gamma */
function chiSqCDF(x: number, df: number): number {
    if (x <= 0) return 0;
    return gammIncP(df / 2, x / 2);
}

/** Inverse chi-squared CDF via bisection */
function chiSqInvCDF(p: number, df: number): number {
    if (p <= 0) return 0;
    if (p >= 1) return Infinity;
    let lo = 0;
    let hi = df + 10 * Math.sqrt(2 * df) + 100;
    for (let i = 0; i < 300; i++) {
        const mid = (lo + hi) / 2;
        const f = chiSqCDF(mid, df) - p;
        if (Math.abs(f) < 1e-12) return mid;
        if (f < 0) lo = mid;
        else hi = mid;
    }
    return (lo + hi) / 2;
}

/** F-distribution CDF via regularized incomplete beta */
function fCDF(x: number, d1: number, d2: number): number {
    if (x <= 0) return 0;
    return betaInc(d1 * x / (d1 * x + d2), d1 / 2, d2 / 2);
}

/** Inverse F CDF via bisection */
function fInvCDF(p: number, d1: number, d2: number): number {
    if (p <= 0) return 0;
    if (p >= 1) return Infinity;
    let lo = 0;
    let hi = 1e6;
    for (let i = 0; i < 300; i++) {
        const mid = (lo + hi) / 2;
        const f = fCDF(mid, d1, d2) - p;
        if (Math.abs(f) < 1e-12) return mid;
        if (f < 0) lo = mid;
        else hi = mid;
    }
    return (lo + hi) / 2;
}

/** Binomial coefficient C(n, k) */
function binomCoef(n: number, k: number): number {
    if (k < 0 || k > n) return 0;
    if (k === 0 || k === n) return 1;
    return Math.exp(gammaLn(n + 1) - gammaLn(k + 1) - gammaLn(n - k + 1));
}

/** Binomial CDF: sum_{i=0}^{k} C(n,i)*p^i*(1-p)^(n-i) */
function binomCDF(k: number, n: number, p: number): number {
    if (k < 0) return 0;
    if (k >= n) return 1;
    // Use regularized incomplete beta: I_{1-p}(n-k, k+1)
    return betaInc(1 - p, n - Math.floor(k), Math.floor(k) + 1);
}

/** Poisson CDF: sum_{i=0}^{k} e^{-lambda} * lambda^i / i! */
function poissonCDF(k: number, lambda: number): number {
    if (k < 0) return 0;
    return gammIncQ(Math.floor(k) + 1, lambda);
}

/** Negative binomial CDF */
function negBinomCDF(k: number, r: number, p: number): number {
    // P(X <= k) where X is failures before r-th success
    if (k < 0) return 0;
    return betaInc(p, r, Math.floor(k) + 1);
}

/** Log-normal CDF */
function lognormCDF(x: number, mean: number, sd: number): number {
    if (x <= 0) return 0;
    return normalCDF((Math.log(x) - mean) / sd);
}

/** Inverse log-normal CDF */
function lognormInvCDF(p: number, mean: number, sd: number): number {
    return Math.exp(mean + sd * normalInvCDF(p));
}

/** Exponential CDF: 1 - e^{-lambda*x} */
function exponCDF(x: number, lambda: number): number {
    if (x < 0) return 0;
    return 1 - Math.exp(-lambda * x);
}

/** Weibull CDF: 1 - e^{-(x/alpha)^beta} */
function weibullCDF(x: number, alpha: number, beta: number): number {
    if (x < 0) return 0;
    return 1 - Math.exp(-Math.pow(x / alpha, beta));
}

/** Weibull PDF: (beta/alpha) * (x/alpha)^(beta-1) * e^{-(x/alpha)^beta} */
function weibullPDF(x: number, alpha: number, beta: number): number {
    if (x < 0) return 0;
    return (beta / alpha) * Math.pow(x / alpha, beta - 1) * Math.exp(-Math.pow(x / alpha, beta));
}

/** Gamma distribution CDF: I(x/beta, alpha) */
function gammaCDF(x: number, alpha: number, beta: number): number {
    if (x < 0) return 0;
    return gammIncP(alpha, x / beta);
}

/** Gamma distribution PDF */
function gammaPDF(x: number, alpha: number, beta: number): number {
    if (x < 0) return 0;
    return Math.exp((alpha - 1) * Math.log(x) - x / beta - alpha * Math.log(beta) - gammaLn(alpha));
}

/** Inverse gamma CDF via bisection */
function gammaInvCDF(p: number, alpha: number, beta: number): number {
    if (p <= 0) return 0;
    if (p >= 1) return Infinity;
    let lo = 0;
    let hi = alpha * beta + 100 * Math.sqrt(alpha) * beta;
    for (let i = 0; i < 300; i++) {
        const mid = (lo + hi) / 2;
        const f = gammaCDF(mid, alpha, beta) - p;
        if (Math.abs(f) < 1e-12) return mid;
        if (f < 0) lo = mid;
        else hi = mid;
    }
    return (lo + hi) / 2;
}

/** Beta distribution CDF */
function betaCDF(x: number, a: number, b: number): number {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    return betaInc(x, a, b);
}

/** Beta distribution PDF */
function betaPDF(x: number, a: number, b: number): number {
    if (x < 0 || x > 1) return 0;
    return Math.exp((a - 1) * Math.log(x) + (b - 1) * Math.log(1 - x) - gammaLn(a) - gammaLn(b) + gammaLn(a + b));
}

/** Hypergeometric distribution: probability P(X = k) */
function hypergeomPMF(k: number, bigN: number, bigK: number, n: number): number {
    return binomCoef(bigK, k) * binomCoef(bigN - bigK, n - k) / binomCoef(bigN, n);
}

/** Hypergeometric CDF: P(X <= k) */
function hypergeomCDF(k: number, bigN: number, bigK: number, n: number): number {
    let sum = 0;
    for (let i = 0; i <= k; i++) {
        sum += hypergeomPMF(i, bigN, bigK, n);
    }
    return sum;
}

// ---------------------------------------------------------------------------
// Bessel functions (polynomial approximations from Abramowitz & Stegun)
// ---------------------------------------------------------------------------

function besselI0(x: number): number {
    const ax = Math.abs(x);
    if (ax < 3.75) {
        const y = (x / 3.75) * (x / 3.75);
        return 1 + y * (3.5156229 + y * (3.0899424 + y * (1.2067492 +
            y * (0.2659732 + y * (0.360768e-1 + y * 0.45813e-2)))));
    }
    const y = 3.75 / ax;
    return (Math.exp(ax) / Math.sqrt(ax)) * (0.39894228 + y * (0.1328592e-1 +
        y * (0.225319e-2 + y * (-0.157565e-2 + y * (0.916281e-2 +
            y * (-0.2057706e-1 + y * (0.2635537e-1 + y * (-0.1647633e-1 + y * 0.392377e-2))))))));
}

function besselI1(x: number): number {
    const ax = Math.abs(x);
    let ans: number;
    if (ax < 3.75) {
        const y = (x / 3.75) * (x / 3.75);
        ans = ax * (0.5 + y * (0.87890594 + y * (0.51498869 + y * (0.15084934 +
            y * (0.2658733e-1 + y * (0.301532e-2 + y * 0.32411e-3))))));
    } else {
        const y = 3.75 / ax;
        ans = (Math.exp(ax) / Math.sqrt(ax)) * (0.39894228 + y * (-0.3988024e-1 +
            y * (-0.362018e-2 + y * (0.163801e-2 + y * (-0.1031555e-1 +
                y * (0.2282967e-1 + y * (-0.2895312e-1 + y * (0.1787654e-1 + y * (-0.420059e-2)))))))));
    }
    return x < 0 ? -ans : ans;
}

function besselIn(n: number, x: number): number {
    if (n === 0) return besselI0(x);
    if (n === 1) return besselI1(x);
    if (x === 0) return 0;
    const ACC = 40;
    const BIGNO = 1e10;
    const BIGNI = 1e-10;
    let bi: number;
    let bip: number;
    let bim: number;
    let ans = 0;
    const tox = 2 / Math.abs(x);
    let bii = 0;
    bip = 0;
    bi = 1;
    for (let j = 2 * (n + Math.round(Math.sqrt(ACC * n))); j > 0; j--) {
        bim = bip + j * tox * bi;
        bip = bi;
        bi = bim;
        if (Math.abs(bi) > BIGNO) {
            ans *= BIGNI;
            bi *= BIGNI;
            bip *= BIGNI;
            bii *= BIGNI;
        }
        if (j === n) bii = bip;
    }
    ans = bii / bi * besselI0(x);
    return (x < 0 && n % 2 === 1) ? -ans : ans;
}

function besselJ0(x: number): number {
    const ax = Math.abs(x);
    if (ax < 8) {
        const y = x * x;
        const p1 = 57568490574.0 + y * (-13362590354.0 + y * (651619640.7 +
            y * (-11214424.18 + y * (77392.33017 + y * (-184.9052456)))));
        const q1 = 57568490411.0 + y * (1029532985.0 + y * (9494680.718 +
            y * (59272.64853 + y * (267.8532712 + y * 1.0))));
        return p1 / q1;
    }
    const z = 8 / ax;
    const y = z * z;
    const xx = ax - 0.785398164;
    const p1 = 1 + y * (-0.1098628627e-2 + y * (0.2734510407e-4 + y * (-0.2073370639e-5 + y * 0.2093887211e-6)));
    const q1 = -0.1562499995e-1 + y * (0.1430488765e-3 + y * (-0.6911147651e-5 + y * (0.7621095161e-6 + y * (-0.934945152e-7))));
    return Math.sqrt(0.636619772 / ax) * (Math.cos(xx) * p1 - z * Math.sin(xx) * q1);
}

function besselJ1(x: number): number {
    const ax = Math.abs(x);
    let ans: number;
    if (ax < 8) {
        const y = x * x;
        const p1 = x * (72362614232.0 + y * (-7895059235.0 + y * (242396853.1 +
            y * (-2972611.439 + y * (15704.4826 + y * (-30.16036606))))));
        const q1 = 144725228442.0 + y * (2300535178.0 + y * (18583304.74 +
            y * (99447.43394 + y * (376.9991397 + y * 1.0))));
        ans = p1 / q1;
    } else {
        const z = 8 / ax;
        const y = z * z;
        const xx = ax - 2.356194491;
        const p1 = 1 + y * (0.183105e-2 + y * (-0.3516396496e-4 + y * (0.2457520174e-5 + y * (-0.240337019e-6))));
        const q1 = 0.04687499995 + y * (-0.2002690873e-3 + y * (0.8449199096e-5 + y * (-0.88228987e-6 + y * 0.105787412e-6)));
        ans = Math.sqrt(0.636619772 / ax) * (Math.cos(xx) * p1 - z * Math.sin(xx) * q1);
    }
    return x < 0 ? -ans : ans;
}

function besselJn(n: number, x: number): number {
    if (n === 0) return besselJ0(x);
    if (n === 1) return besselJ1(x);
    if (x === 0) return 0;
    const tox = 2 / Math.abs(x);
    let bjm = besselJ0(Math.abs(x));
    let bj = besselJ1(Math.abs(x));
    for (let j = 1; j < n; j++) {
        const bjp = j * tox * bj - bjm;
        bjm = bj;
        bj = bjp;
    }
    return (x < 0 && n % 2 === 1) ? -bj : bj;
}

function besselK0(x: number): number {
    if (x <= 0) return Infinity;
    if (x <= 2) {
        const y = x * x / 4;
        return (-Math.log(x / 2) * besselI0(x)) + (-0.57721566 + y * (0.42278420 +
            y * (0.23069756 + y * (0.3488590e-1 + y * (0.262698e-2 + y * (0.10750e-3 + y * 0.74e-5))))));
    }
    const y = 2 / x;
    return (Math.exp(-x) / Math.sqrt(x)) * (1.25331414 + y * (-0.7832358e-1 +
        y * (0.2189568e-1 + y * (-0.1062446e-1 + y * (0.587872e-2 + y * (-0.251540e-2 + y * 0.53208e-3))))));
}

function besselK1(x: number): number {
    if (x <= 0) return Infinity;
    if (x <= 2) {
        const y = x * x / 4;
        return (Math.log(x / 2) * besselI1(x)) + (1 / x) * (1 + y * (0.15443144 +
            y * (-0.67278579 + y * (-0.18156897 + y * (-0.1919402e-1 + y * (-0.110404e-2 + y * (-0.4686e-4)))))));
    }
    const y = 2 / x;
    return (Math.exp(-x) / Math.sqrt(x)) * (1.25331414 + y * (0.23498619 +
        y * (-0.3655620e-1 + y * (0.1504268e-1 + y * (-0.780353e-2 + y * (0.325614e-2 + y * (-0.68245e-3)))))));
}

function besselKn(n: number, x: number): number {
    if (n === 0) return besselK0(x);
    if (n === 1) return besselK1(x);
    if (x <= 0) return Infinity;
    const tox = 2 / x;
    let bkm = besselK0(x);
    let bk = besselK1(x);
    for (let j = 1; j < n; j++) {
        const bkp = bkm + j * tox * bk;
        bkm = bk;
        bk = bkp;
    }
    return bk;
}

function besselY0(x: number): number {
    if (x <= 0) return -Infinity;
    if (x < 8) {
        const y = x * x;
        const p1 = -2957821389.0 + y * (7062834065.0 + y * (-512359803.6 +
            y * (10879881.29 + y * (-86327.92757 + y * 228.4622733))));
        const q1 = 40076544269.0 + y * (745249964.8 + y * (7189466.438 +
            y * (47447.26560 + y * (226.1030244 + y * 1.0))));
        return (p1 / q1) + (0.636619772 * besselJ0(x) * Math.log(x));
    }
    const z = 8 / x;
    const y = z * z;
    const xx = x - 0.785398164;
    const p1 = 1 + y * (-0.1098628627e-2 + y * (0.2734510407e-4 + y * (-0.2073370639e-5 + y * 0.2093887211e-6)));
    const q1 = -0.1562499995e-1 + y * (0.1430488765e-3 + y * (-0.6911147651e-5 + y * (0.7621095161e-6 - y * 0.934945152e-7)));
    return Math.sqrt(0.636619772 / x) * (Math.sin(xx) * p1 + z * Math.cos(xx) * q1);
}

function besselY1(x: number): number {
    if (x <= 0) return -Infinity;
    if (x < 8) {
        const y = x * x;
        const p1 = x * (-0.4900604943e13 + y * (0.1275274390e13 + y * (-0.5153438139e11 +
            y * (0.7349264551e9 + y * (-0.4237922726e7 + y * 0.8511937935e4)))));
        const q1 = 0.2499580570e14 + y * (0.4244419664e12 + y * (0.3733650367e10 +
            y * (0.2245904002e8 + y * (0.1020426050e6 + y * (0.3549632885e3 + y)))));
        return (p1 / q1) + 0.636619772 * (besselJ1(x) * Math.log(x) - 1 / x);
    }
    const z = 8 / x;
    const y = z * z;
    const xx = x - 2.356194491;
    const p1 = 1 + y * (0.183105e-2 + y * (-0.3516396496e-4 + y * (0.2457520174e-5 + y * (-0.240337019e-6))));
    const q1 = 0.04687499995 + y * (-0.2002690873e-3 + y * (0.8449199096e-5 + y * (-0.88228987e-6 + y * 0.105787412e-6)));
    return Math.sqrt(0.636619772 / x) * (Math.sin(xx) * p1 + z * Math.cos(xx) * q1);
}

function besselYn(n: number, x: number): number {
    if (n === 0) return besselY0(x);
    if (n === 1) return besselY1(x);
    if (x <= 0) return -Infinity;
    const tox = 2 / x;
    let bym = besselY0(x);
    let by = besselY1(x);
    for (let j = 1; j < n; j++) {
        const byp = j * tox * by - bym;
        bym = by;
        by = byp;
    }
    return by;
}

// ---------------------------------------------------------------------------
// Criteria matching helper (reused across SUMIF, COUNTIF, etc.)
// ---------------------------------------------------------------------------

function matchesCriteria(value: FormulaValue, criteria: FormulaValue): boolean {
    const criteriaStr = String(criteria ?? '');

    // Check for operator prefixes: >=, <=, <>, >, <, =
    const match = /^(>=|<=|<>|>|<|=)(.*)$/.exec(criteriaStr);
    if (match) {
        const op = match[1];
        const rhs = match[2];
        const rhsNum = Number(rhs);
        const isRhsNum = !isNaN(rhsNum) && rhs.trim() !== '';

        const valNum = typeof value === 'number' ? value : Number(String(value ?? ''));
        const valStr = String(value ?? '').toLowerCase();
        const rhsLower = rhs.toLowerCase();

        if (isRhsNum) {
            const v = isNaN(valNum) ? 0 : valNum;
            switch (op) {
                case '>=': return v >= rhsNum;
                case '<=': return v <= rhsNum;
                case '<>': return v !== rhsNum;
                case '>': return v > rhsNum;
                case '<': return v < rhsNum;
                case '=': return v === rhsNum;
            }
        } else {
            switch (op) {
                case '>=': return valStr >= rhsLower;
                case '<=': return valStr <= rhsLower;
                case '<>': return valStr !== rhsLower;
                case '>': return valStr > rhsLower;
                case '<': return valStr < rhsLower;
                case '=': return valStr === rhsLower;
            }
        }
    }

    // Wildcard matching: * and ?
    if (criteriaStr.includes('*') || criteriaStr.includes('?')) {
        const regexStr = '^' + criteriaStr
            .replace(/[.+^${}()|[\]\\]/g, '\\$&')
            .replace(/\*/g, '.*')
            .replace(/\?/g, '.') + '$';
        try {
            const re = new RegExp(regexStr, 'i');
            return re.test(String(value ?? ''));
        } catch {
            return false;
        }
    }

    // Exact match - compare numerically if both are numeric, otherwise string
    const criteriaNum = Number(criteriaStr);
    const isCriteriaNum = !isNaN(criteriaNum) && criteriaStr.trim() !== '';

    if (isCriteriaNum && typeof value === 'number') {
        return value === criteriaNum;
    }
    if (isCriteriaNum && typeof value === 'string') {
        const vn = Number(value);
        return !isNaN(vn) && vn === criteriaNum;
    }
    return String(value ?? '').toLowerCase() === criteriaStr.toLowerCase();
}

/** Flatten a FormulaValue that is expected to be an array of values for range-based functions */
function flattenRange(val: FormulaValue): FormulaValue[] {
    if (Array.isArray(val)) return val;
    return [val];
}

// ---------------------------------------------------------------------------
// Main registration
// ---------------------------------------------------------------------------

export function registerStatisticalFunctions(registry: FunctionRegistry): void {
    // -----------------------------------------------------------------------
    // AVERAGE / AVERAGEA / AVEDEV / AVERAGEIF
    // -----------------------------------------------------------------------

    registry.register('AVERAGE', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'AVERAGE requires at least one number');
        return smartRound(nums.reduce((a, b) => a + b, 0) / nums.length);
    });

    registry.register('AVERAGEA', (...args: FormulaValue[]) => {
        // Like AVERAGEA: counts text as 0, booleans as 1/0, nulls skipped
        let sum = 0;
        let count = 0;
        for (const arg of args) {
            const values = Array.isArray(arg) ? arg : [arg];
            for (const v of values) {
                if (v === null) continue;
                if (typeof v === 'number') { sum += v; count++; } else if (typeof v === 'boolean') { sum += v ? 1 : 0; count++; } else if (typeof v === 'string') { sum += 0; count++; }
            }
        }
        if (count === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'AVERAGEA requires at least one value');
        return smartRound(sum / count);
    });

    registry.register('AVEDEV', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) throw new FormulaError(FormulaErrorType.NUM, 'AVEDEV requires at least one number');
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        const dev = nums.reduce((a, b) => a + Math.abs(b - avg), 0) / nums.length;
        return smartRound(dev);
    });

    registry.register('AVERAGEIF', (...args: FormulaValue[]) => {
        const range = flattenRange(args[0]);
        const criteria = args[1];
        const avgRange = args[2] !== undefined ? flattenRange(args[2]) : range;
        let sum = 0;
        let count = 0;
        for (let i = 0; i < range.length; i++) {
            if (matchesCriteria(range[i], criteria)) {
                const v = toNum(avgRange[i] ?? null);
                sum += v;
                count++;
            }
        }
        if (count === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'AVERAGEIF: no matching cells');
        return smartRound(sum / count);
    });

    // -----------------------------------------------------------------------
    // COUNT / COUNTA / COUNTBLANK / COUNTIF / COUNTIFS
    // -----------------------------------------------------------------------

    registry.register('COUNT', (...args: FormulaValue[]) => {
        let count = 0;
        for (const arg of args) {
            if (Array.isArray(arg)) {
                for (const v of arg) {
                    if (typeof v === 'number' && !isNaN(v)) count++;
                }
            } else if (typeof arg === 'number' && !isNaN(arg)) {
                count++;
            } else if (typeof arg === 'string') {
                const n = Number(arg);
                if (!isNaN(n) && arg.trim() !== '') count++;
            }
        }
        return count;
    });

    registry.register('COUNTA', (...args: FormulaValue[]) => {
        let count = 0;
        for (const arg of args) {
            if (Array.isArray(arg)) {
                // number[] elements are always non-blank (array cells are populated)
                count += arg.length;
            } else if (arg !== null && arg !== undefined && arg !== '') {
                count++;
            }
        }
        return count;
    });

    registry.register('COUNTBLANK', (...args: FormulaValue[]) => {
        let count = 0;
        for (const arg of args) {
            if (Array.isArray(arg)) {
                // number[] elements are never blank
                void arg;
            } else if (arg === null || arg === undefined || arg === '') {
                count++;
            }
        }
        return count;
    });

    registry.register('COUNTIF', (...args: FormulaValue[]) => {
        const range = flattenRange(args[0]);
        const criteria = args[1];
        let count = 0;
        for (const v of range) {
            if (matchesCriteria(v, criteria)) count++;
        }
        return count;
    });

    registry.register('COUNTIFS', (...args: FormulaValue[]) => {
        if (args.length < 2 || args.length % 2 !== 0) {
            throw new FormulaError(FormulaErrorType.VALUE, 'COUNTIFS requires pairs of range,criteria');
        }
        const pairs: Array<{ range: FormulaValue[]; criteria: FormulaValue }> = [];
        for (let i = 0; i < args.length; i += 2) {
            pairs.push({ range: flattenRange(args[i]), criteria: args[i + 1] });
        }
        const len = pairs[0].range.length;
        let count = 0;
        for (let i = 0; i < len; i++) {
            let allMatch = true;
            for (const pair of pairs) {
                if (!matchesCriteria(pair.range[i] ?? null, pair.criteria)) {
                    allMatch = false;
                    break;
                }
            }
            if (allMatch) count++;
        }
        return count;
    });

    // -----------------------------------------------------------------------
    // SUMIF / SUMIFS
    // -----------------------------------------------------------------------

    registry.register('SUMIF', (...args: FormulaValue[]) => {
        const range = flattenRange(args[0]);
        const criteria = args[1];
        const sumRange = args[2] !== undefined ? flattenRange(args[2]) : range;
        let sum = 0;
        for (let i = 0; i < range.length; i++) {
            if (matchesCriteria(range[i], criteria)) {
                sum += toNum(sumRange[i] ?? null);
            }
        }
        return smartRound(sum);
    });

    registry.register('SUMIFS', (...args: FormulaValue[]) => {
        if (args.length < 3) throw new FormulaError(FormulaErrorType.VALUE, 'SUMIFS requires at least 3 arguments');
        const sumRange = flattenRange(args[0]);
        const pairs: Array<{ range: FormulaValue[]; criteria: FormulaValue }> = [];
        for (let i = 1; i < args.length; i += 2) {
            pairs.push({ range: flattenRange(args[i]), criteria: args[i + 1] });
        }
        let sum = 0;
        for (let i = 0; i < sumRange.length; i++) {
            let allMatch = true;
            for (const pair of pairs) {
                if (!matchesCriteria(pair.range[i] ?? null, pair.criteria)) {
                    allMatch = false;
                    break;
                }
            }
            if (allMatch) sum += toNum(sumRange[i] ?? null);
        }
        return smartRound(sum);
    });

    // -----------------------------------------------------------------------
    // MAX / MIN / MAXA / MINA / MAXIFS / MINIFS / MEDIAN / LARGE / SMALL
    // -----------------------------------------------------------------------

    registry.register('MAX', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) return 0;
        return Math.max(...nums);
    });

    registry.register('MAXA', (...args: FormulaValue[]) => {
        let max = -Infinity;
        let hasVal = false;
        for (const arg of args) {
            const values = Array.isArray(arg) ? arg : [arg];
            for (const v of values) {
                if (v === null || v === undefined) continue;
                let n: number;
                if (typeof v === 'number') n = v;
                else if (typeof v === 'boolean') n = v ? 1 : 0;
                else n = 0;
                if (n > max) max = n;
                hasVal = true;
            }
        }
        return hasVal ? max : 0;
    });

    registry.register('MAXIFS', (...args: FormulaValue[]) => {
        if (args.length < 3) throw new FormulaError(FormulaErrorType.VALUE, 'MAXIFS requires at least 3 arguments');
        const maxRange = flattenRange(args[0]);
        const pairs: Array<{ range: FormulaValue[]; criteria: FormulaValue }> = [];
        for (let i = 1; i < args.length; i += 2) {
            pairs.push({ range: flattenRange(args[i]), criteria: args[i + 1] });
        }
        let max = -Infinity;
        let found = false;
        for (let i = 0; i < maxRange.length; i++) {
            let allMatch = true;
            for (const pair of pairs) {
                if (!matchesCriteria(pair.range[i] ?? null, pair.criteria)) {
                    allMatch = false;
                    break;
                }
            }
            if (allMatch) {
                const v = toNum(maxRange[i] ?? null);
                if (v > max) max = v;
                found = true;
            }
        }
        return found ? max : 0;
    });

    registry.register('MIN', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) return 0;
        return Math.min(...nums);
    });

    registry.register('MINA', (...args: FormulaValue[]) => {
        let min = Infinity;
        let hasVal = false;
        for (const arg of args) {
            const values = Array.isArray(arg) ? arg : [arg];
            for (const v of values) {
                if (v === null || v === undefined) continue;
                let n: number;
                if (typeof v === 'number') n = v;
                else if (typeof v === 'boolean') n = v ? 1 : 0;
                else n = 0;
                if (n < min) min = n;
                hasVal = true;
            }
        }
        return hasVal ? min : 0;
    });

    registry.register('MINIFS', (...args: FormulaValue[]) => {
        if (args.length < 3) throw new FormulaError(FormulaErrorType.VALUE, 'MINIFS requires at least 3 arguments');
        const minRange = flattenRange(args[0]);
        const pairs: Array<{ range: FormulaValue[]; criteria: FormulaValue }> = [];
        for (let i = 1; i < args.length; i += 2) {
            pairs.push({ range: flattenRange(args[i]), criteria: args[i + 1] });
        }
        let min = Infinity;
        let found = false;
        for (let i = 0; i < minRange.length; i++) {
            let allMatch = true;
            for (const pair of pairs) {
                if (!matchesCriteria(pair.range[i] ?? null, pair.criteria)) {
                    allMatch = false;
                    break;
                }
            }
            if (allMatch) {
                const v = toNum(minRange[i] ?? null);
                if (v < min) min = v;
                found = true;
            }
        }
        return found ? min : 0;
    });

    registry.register('MEDIAN', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args).slice().sort((a, b) => a - b);
        if (nums.length === 0) throw new FormulaError(FormulaErrorType.NUM, 'MEDIAN requires at least one number');
        const mid = Math.floor(nums.length / 2);
        if (nums.length % 2 === 0) return smartRound((nums[mid - 1] + nums[mid]) / 2);
        return nums[mid];
    });

    registry.register('LARGE', (...args: FormulaValue[]) => {
        const nums = flattenArgs([args[0]]).slice().sort((a, b) => b - a);
        const k = Math.round(toNum(args[1]));
        if (k < 1 || k > nums.length) throw new FormulaError(FormulaErrorType.NUM, 'LARGE: k out of range');
        return nums[k - 1];
    });

    registry.register('SMALL', (...args: FormulaValue[]) => {
        const nums = flattenArgs([args[0]]).slice().sort((a, b) => a - b);
        const k = Math.round(toNum(args[1]));
        if (k < 1 || k > nums.length) throw new FormulaError(FormulaErrorType.NUM, 'SMALL: k out of range');
        return nums[k - 1];
    });

    // -----------------------------------------------------------------------
    // STDEV / VAR family
    // -----------------------------------------------------------------------

    function sampleVariance(nums: number[]): number {
        if (nums.length < 2) return NaN;
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        return nums.reduce((a, b) => a + (b - avg) ** 2, 0) / (nums.length - 1);
    }

    function populationVariance(nums: number[]): number {
        if (nums.length === 0) return NaN;
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        return nums.reduce((a, b) => a + (b - avg) ** 2, 0) / nums.length;
    }

    registry.register('VAR', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = sampleVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'VAR requires at least 2 numbers');
        return smartRound(v);
    });

    registry.register('VAR.S', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = sampleVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'VAR.S requires at least 2 numbers');
        return smartRound(v);
    });

    registry.register('VARA', (...args: FormulaValue[]) => {
        const vals: number[] = [];
        for (const arg of args) {
            const list = Array.isArray(arg) ? arg : [arg];
            for (const v of list) {
                if (v === null || v === undefined) continue;
                if (typeof v === 'number') vals.push(v);
                else if (typeof v === 'boolean') vals.push(v ? 1 : 0);
                else vals.push(0);
            }
        }
        const v = sampleVariance(vals);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'VARA requires at least 2 values');
        return smartRound(v);
    });

    registry.register('VARP', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = populationVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NUM, 'VARP requires at least one number');
        return smartRound(v);
    });

    registry.register('VAR.P', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = populationVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NUM, 'VAR.P requires at least one number');
        return smartRound(v);
    });

    registry.register('VARPA', (...args: FormulaValue[]) => {
        const vals: number[] = [];
        for (const arg of args) {
            const list = Array.isArray(arg) ? arg : [arg];
            for (const v of list) {
                if (v === null || v === undefined) continue;
                if (typeof v === 'number') vals.push(v);
                else if (typeof v === 'boolean') vals.push(v ? 1 : 0);
                else vals.push(0);
            }
        }
        const v = populationVariance(vals);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NUM, 'VARPA requires at least one value');
        return smartRound(v);
    });

    registry.register('STDEV', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = sampleVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'STDEV requires at least 2 numbers');
        return smartRound(Math.sqrt(v));
    });

    registry.register('STDEV.S', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = sampleVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'STDEV.S requires at least 2 numbers');
        return smartRound(Math.sqrt(v));
    });

    registry.register('STDEVA', (...args: FormulaValue[]) => {
        const vals: number[] = [];
        for (const arg of args) {
            const list = Array.isArray(arg) ? arg : [arg];
            for (const v of list) {
                if (v === null || v === undefined) continue;
                if (typeof v === 'number') vals.push(v);
                else if (typeof v === 'boolean') vals.push(v ? 1 : 0);
                else vals.push(0);
            }
        }
        const v = sampleVariance(vals);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'STDEVA requires at least 2 values');
        return smartRound(Math.sqrt(v));
    });

    registry.register('STDEVP', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = populationVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NUM, 'STDEVP requires at least one number');
        return smartRound(Math.sqrt(v));
    });

    registry.register('STDEV.P', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const v = populationVariance(nums);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NUM, 'STDEV.P requires at least one number');
        return smartRound(Math.sqrt(v));
    });

    registry.register('STDEVPA', (...args: FormulaValue[]) => {
        const vals: number[] = [];
        for (const arg of args) {
            const list = Array.isArray(arg) ? arg : [arg];
            for (const v of list) {
                if (v === null || v === undefined) continue;
                if (typeof v === 'number') vals.push(v);
                else if (typeof v === 'boolean') vals.push(v ? 1 : 0);
                else vals.push(0);
            }
        }
        const v = populationVariance(vals);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NUM, 'STDEVPA requires at least one value');
        return smartRound(Math.sqrt(v));
    });

    registry.register('DEVSQ', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) return 0;
        const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
        return smartRound(nums.reduce((a, b) => a + (b - avg) ** 2, 0));
    });

    // -----------------------------------------------------------------------
    // COVAR / COVARIANCE.P / COVARIANCE.S / CORREL / PEARSON
    // -----------------------------------------------------------------------

    function covarianceP(xs: number[], ys: number[]): number {
        if (xs.length !== ys.length || xs.length === 0) return NaN;
        const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
        const my = ys.reduce((a, b) => a + b, 0) / ys.length;
        return xs.reduce((a, v, i) => a + (v - mx) * (ys[i] - my), 0) / xs.length;
    }

    function covarianceS(xs: number[], ys: number[]): number {
        if (xs.length !== ys.length || xs.length < 2) return NaN;
        const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
        const my = ys.reduce((a, b) => a + b, 0) / ys.length;
        return xs.reduce((a, v, i) => a + (v - mx) * (ys[i] - my), 0) / (xs.length - 1);
    }

    registry.register('COVAR', (...args: FormulaValue[]) => {
        const xs = flattenArgs([args[0]]);
        const ys = flattenArgs([args[1]]);
        const v = covarianceP(xs, ys);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NA, 'COVAR: arrays must have equal length');
        return smartRound(v);
    });

    registry.register('COVARIANCE.P', (...args: FormulaValue[]) => {
        const xs = flattenArgs([args[0]]);
        const ys = flattenArgs([args[1]]);
        const v = covarianceP(xs, ys);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NA, 'COVARIANCE.P: arrays must have equal length');
        return smartRound(v);
    });

    registry.register('COVARIANCE.S', (...args: FormulaValue[]) => {
        const xs = flattenArgs([args[0]]);
        const ys = flattenArgs([args[1]]);
        const v = covarianceS(xs, ys);
        if (isNaN(v)) throw new FormulaError(FormulaErrorType.NA, 'COVARIANCE.S: arrays must have equal length >= 2');
        return smartRound(v);
    });

    registry.register('CORREL', (...args: FormulaValue[]) => {
        const xs = flattenArgs([args[0]]);
        const ys = flattenArgs([args[1]]);
        const cov = covarianceP(xs, ys);
        const sx = Math.sqrt(populationVariance(xs));
        const sy = Math.sqrt(populationVariance(ys));
        if (sx === 0 || sy === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'CORREL: zero variance');
        return smartRound(cov / (sx * sy));
    });

    registry.register('PEARSON', (...args: FormulaValue[]) => {
        const xs = flattenArgs([args[0]]);
        const ys = flattenArgs([args[1]]);
        const cov = covarianceP(xs, ys);
        const sx = Math.sqrt(populationVariance(xs));
        const sy = Math.sqrt(populationVariance(ys));
        if (sx === 0 || sy === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'PEARSON: zero variance');
        return smartRound(cov / (sx * sy));
    });

    registry.register('RSQ', (...args: FormulaValue[]) => {
        const xs = flattenArgs([args[0]]);
        const ys = flattenArgs([args[1]]);
        const cov = covarianceP(xs, ys);
        const sx = Math.sqrt(populationVariance(xs));
        const sy = Math.sqrt(populationVariance(ys));
        if (sx === 0 || sy === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'RSQ: zero variance');
        return smartRound((cov / (sx * sy)) ** 2);
    });

    // -----------------------------------------------------------------------
    // SLOPE / STEYX / STANDARDIZE / SKEW / SKEW.P
    // -----------------------------------------------------------------------

    registry.register('SLOPE', (...args: FormulaValue[]) => {
        const ys = flattenArgs([args[0]]);
        const xs = flattenArgs([args[1]]);
        const cov = covarianceP(xs, ys);
        const varX = populationVariance(xs);
        if (varX === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'SLOPE: zero variance in x');
        return smartRound(cov / varX);
    });

    registry.register('STEYX', (...args: FormulaValue[]) => {
        const ys = flattenArgs([args[0]]);
        const xs = flattenArgs([args[1]]);
        const n = xs.length;
        if (n < 3 || n !== ys.length) throw new FormulaError(FormulaErrorType.NA, 'STEYX: arrays must have equal length >= 3');
        const my = ys.reduce((a, b) => a + b, 0) / n;
        const mx = xs.reduce((a, b) => a + b, 0) / n;
        const ssxy = xs.reduce((a, v, i) => a + (v - mx) * (ys[i] - my), 0);
        const ssxx = xs.reduce((a, v) => a + (v - mx) ** 2, 0);
        const ssyy = ys.reduce((a, v) => a + (v - my) ** 2, 0);
        const result = Math.sqrt((ssyy - ssxy * ssxy / ssxx) / (n - 2));
        return smartRound(result);
    });

    registry.register('STANDARDIZE', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        if (sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'STANDARDIZE: sigma must be > 0');
        return smartRound((x - mean) / sd);
    });

    registry.register('SKEW', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const n = nums.length;
        if (n < 3) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'SKEW requires at least 3 numbers');
        const avg = nums.reduce((a, b) => a + b, 0) / n;
        const s = Math.sqrt(sampleVariance(nums));
        if (s === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'SKEW: zero standard deviation');
        const m3 = nums.reduce((a, b) => a + ((b - avg) / s) ** 3, 0);
        return smartRound((n / ((n - 1) * (n - 2))) * m3);
    });

    registry.register('SKEW.P', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        const n = nums.length;
        if (n < 3) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'SKEW.P requires at least 3 numbers');
        const avg = nums.reduce((a, b) => a + b, 0) / n;
        const s = Math.sqrt(populationVariance(nums));
        if (s === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'SKEW.P: zero standard deviation');
        const m3 = nums.reduce((a, b) => a + ((b - avg) / s) ** 3, 0) / n;
        return smartRound(m3);
    });

    registry.register('GEOMEAN', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) throw new FormulaError(FormulaErrorType.NUM, 'GEOMEAN requires at least one number');
        if (nums.some(v => v <= 0)) throw new FormulaError(FormulaErrorType.NUM, 'GEOMEAN: all values must be positive');
        const logSum = nums.reduce((a, b) => a + Math.log(b), 0);
        return smartRound(Math.exp(logSum / nums.length));
    });

    registry.register('HARMEAN', (...args: FormulaValue[]) => {
        const nums = flattenArgs(args);
        if (nums.length === 0) throw new FormulaError(FormulaErrorType.NUM, 'HARMEAN requires at least one number');
        if (nums.some(v => v <= 0)) throw new FormulaError(FormulaErrorType.NUM, 'HARMEAN: all values must be positive');
        const invSum = nums.reduce((a, b) => a + 1 / b, 0);
        return smartRound(nums.length / invSum);
    });

    // -----------------------------------------------------------------------
    // Normal distribution functions
    // -----------------------------------------------------------------------

    registry.register('NORM.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        const cumulative = args[3];
        if (sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'NORM.DIST: sigma must be > 0');
        const z = (x - mean) / sd;
        if (cumulative === false || cumulative === 0) {
            return smartRound(Math.exp(-0.5 * z * z) / (sd * Math.sqrt(2 * Math.PI)));
        }
        return smartRound(normalCDF(z));
    });

    registry.register('NORMDIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        const cumulative = args[3];
        if (sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'NORMDIST: sigma must be > 0');
        const z = (x - mean) / sd;
        if (cumulative === false || cumulative === 0) {
            return smartRound(Math.exp(-0.5 * z * z) / (sd * Math.sqrt(2 * Math.PI)));
        }
        return smartRound(normalCDF(z));
    });

    registry.register('NORM.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        if (p <= 0 || p >= 1) throw new FormulaError(FormulaErrorType.NUM, 'NORM.INV: p must be between 0 and 1');
        if (sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'NORM.INV: sigma must be > 0');
        return smartRound(mean + sd * normalInvCDF(p));
    });

    registry.register('NORMINV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        if (p <= 0 || p >= 1) throw new FormulaError(FormulaErrorType.NUM, 'NORMINV: p must be between 0 and 1');
        if (sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'NORMINV: sigma must be > 0');
        return smartRound(mean + sd * normalInvCDF(p));
    });

    registry.register('NORM.S.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const cumulative = args[1];
        if (cumulative === false || cumulative === 0) {
            return smartRound(Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI));
        }
        return smartRound(normalCDF(x));
    });

    registry.register('NORMSDIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        return smartRound(normalCDF(x));
    });

    registry.register('NORM.S.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        if (p <= 0 || p >= 1) throw new FormulaError(FormulaErrorType.NUM, 'NORM.S.INV: p must be between 0 and 1');
        return smartRound(normalInvCDF(p));
    });

    registry.register('NORMSINV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        if (p <= 0 || p >= 1) throw new FormulaError(FormulaErrorType.NUM, 'NORMSINV: p must be between 0 and 1');
        return smartRound(normalInvCDF(p));
    });

    registry.register('PHI', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        return smartRound(Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI));
    });

    registry.register('GAUSS', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        return smartRound(normalCDF(x) - 0.5);
    });

    // -----------------------------------------------------------------------
    // Student's t distribution
    // -----------------------------------------------------------------------

    registry.register('T.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const df = toNum(args[1]);
        const cumulative = args[2];
        if (df < 1) throw new FormulaError(FormulaErrorType.NUM, 'T.DIST: df must be >= 1');
        if (cumulative === false || cumulative === 0) {
            // PDF
            return smartRound(Math.exp(gammaLn((df + 1) / 2) - gammaLn(df / 2)) /
                (Math.sqrt(df * Math.PI) * Math.pow(1 + x * x / df, (df + 1) / 2)));
        }
        return smartRound(tCDF(x, df));
    });

    registry.register('T.DIST.RT', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const df = toNum(args[1]);
        if (df < 1) throw new FormulaError(FormulaErrorType.NUM, 'T.DIST.RT: df must be >= 1');
        return smartRound(1 - tCDF(x, df));
    });

    registry.register('T.DIST.2T', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const df = toNum(args[1]);
        if (df < 1) throw new FormulaError(FormulaErrorType.NUM, 'T.DIST.2T: df must be >= 1');
        if (x < 0) throw new FormulaError(FormulaErrorType.NUM, 'T.DIST.2T: x must be >= 0');
        return smartRound(2 * (1 - tCDF(Math.abs(x), df)));
    });

    registry.register('TDIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const df = toNum(args[1]);
        const tails = toNum(args[2]);
        if (df < 1) throw new FormulaError(FormulaErrorType.NUM, 'TDIST: df must be >= 1');
        if (x < 0) throw new FormulaError(FormulaErrorType.NUM, 'TDIST: x must be >= 0');
        if (tails === 1) return smartRound(1 - tCDF(x, df));
        if (tails === 2) return smartRound(2 * (1 - tCDF(x, df)));
        throw new FormulaError(FormulaErrorType.NUM, 'TDIST: tails must be 1 or 2');
    });

    registry.register('T.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const df = toNum(args[1]);
        if (p <= 0 || p >= 1) throw new FormulaError(FormulaErrorType.NUM, 'T.INV: p must be between 0 and 1');
        if (df < 1) throw new FormulaError(FormulaErrorType.NUM, 'T.INV: df must be >= 1');
        return smartRound(tInvCDF(p, df));
    });

    registry.register('T.INV.2T', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const df = toNum(args[1]);
        if (p <= 0 || p >= 1) throw new FormulaError(FormulaErrorType.NUM, 'T.INV.2T: p must be between 0 and 1');
        if (df < 1) throw new FormulaError(FormulaErrorType.NUM, 'T.INV.2T: df must be >= 1');
        // Two-tailed: find t such that 2*(1-CDF(t)) = p => CDF(t) = 1 - p/2
        return smartRound(Math.abs(tInvCDF(1 - p / 2, df)));
    });

    registry.register('TINV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const df = toNum(args[1]);
        if (p <= 0 || p >= 1) throw new FormulaError(FormulaErrorType.NUM, 'TINV: p must be between 0 and 1');
        if (df < 1) throw new FormulaError(FormulaErrorType.NUM, 'TINV: df must be >= 1');
        // Legacy TINV is two-tailed
        return smartRound(Math.abs(tInvCDF(1 - p / 2, df)));
    });

    registry.register('T.TEST', (...args: FormulaValue[]) => {
        const arr1 = flattenArgs([args[0]]);
        const arr2 = flattenArgs([args[1]]);
        const tails = toNum(args[2]);
        const testType = toNum(args[3]);
        if (arr1.length < 2 || arr2.length < 2) throw new FormulaError(FormulaErrorType.NA, 'T.TEST: arrays must have at least 2 elements');

        const n1 = arr1.length;
        const n2 = arr2.length;
        const m1 = arr1.reduce((a, b) => a + b, 0) / n1;
        const m2 = arr2.reduce((a, b) => a + b, 0) / n2;
        let tStat: number;
        let df: number;

        if (testType === 1) {
            // Paired
            if (n1 !== n2) throw new FormulaError(FormulaErrorType.NA, 'T.TEST: paired test requires equal length arrays');
            const diffs = arr1.map((v, i) => v - arr2[i]);
            const md = diffs.reduce((a, b) => a + b, 0) / n1;
            const sd = Math.sqrt(sampleVariance(diffs));
            tStat = md / (sd / Math.sqrt(n1));
            df = n1 - 1;
        } else if (testType === 2) {
            // Two-sample equal variance
            const sp2 = ((n1 - 1) * sampleVariance(arr1) + (n2 - 1) * sampleVariance(arr2)) / (n1 + n2 - 2);
            tStat = (m1 - m2) / Math.sqrt(sp2 * (1 / n1 + 1 / n2));
            df = n1 + n2 - 2;
        } else {
            // Two-sample unequal variance (Welch)
            const v1 = sampleVariance(arr1);
            const v2 = sampleVariance(arr2);
            tStat = (m1 - m2) / Math.sqrt(v1 / n1 + v2 / n2);
            df = (v1 / n1 + v2 / n2) ** 2 /
                ((v1 / n1) ** 2 / (n1 - 1) + (v2 / n2) ** 2 / (n2 - 1));
        }

        const pOneTail = 1 - tCDF(Math.abs(tStat), df);
        return smartRound(tails === 1 ? pOneTail : 2 * pOneTail);
    });

    registry.register('TTEST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('T.TEST')!(...args);
    });

    // -----------------------------------------------------------------------
    // Chi-squared distribution
    // -----------------------------------------------------------------------

    registry.register('CHISQ.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const df = toNum(args[1]);
        const cumulative = args[2];
        if (x < 0 || df < 1) throw new FormulaError(FormulaErrorType.NUM, 'CHISQ.DIST: x >= 0 and df >= 1 required');
        if (cumulative === false || cumulative === 0) {
            // PDF
            return smartRound(Math.exp((df / 2 - 1) * Math.log(x) - x / 2 - (df / 2) * Math.log(2) - gammaLn(df / 2)));
        }
        return smartRound(chiSqCDF(x, df));
    });

    registry.register('CHISQ.DIST.RT', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const df = toNum(args[1]);
        if (x < 0 || df < 1) throw new FormulaError(FormulaErrorType.NUM, 'CHISQ.DIST.RT: x >= 0 and df >= 1 required');
        return smartRound(1 - chiSqCDF(x, df));
    });

    registry.register('CHIDIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const df = toNum(args[1]);
        if (x < 0 || df < 1) throw new FormulaError(FormulaErrorType.NUM, 'CHIDIST: x >= 0 and df >= 1 required');
        return smartRound(1 - chiSqCDF(x, df));
    });

    registry.register('CHISQ.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const df = toNum(args[1]);
        if (p < 0 || p >= 1 || df < 1) throw new FormulaError(FormulaErrorType.NUM, 'CHISQ.INV: 0 <= p < 1 and df >= 1 required');
        return smartRound(chiSqInvCDF(p, df));
    });

    registry.register('CHISQ.INV.RT', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const df = toNum(args[1]);
        if (p <= 0 || p > 1 || df < 1) throw new FormulaError(FormulaErrorType.NUM, 'CHISQ.INV.RT: 0 < p <= 1 and df >= 1 required');
        return smartRound(chiSqInvCDF(1 - p, df));
    });

    registry.register('CHIINV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const df = toNum(args[1]);
        if (p <= 0 || p > 1 || df < 1) throw new FormulaError(FormulaErrorType.NUM, 'CHIINV: 0 < p <= 1 and df >= 1 required');
        return smartRound(chiSqInvCDF(1 - p, df));
    });

    registry.register('CHISQ.TEST', (...args: FormulaValue[]) => {
        const observed = flattenArgs([args[0]]);
        const expected = flattenArgs([args[1]]);
        if (observed.length !== expected.length || observed.length < 2) {
            throw new FormulaError(FormulaErrorType.NA, 'CHISQ.TEST: arrays must have equal length >= 2');
        }
        const chiSq = observed.reduce((a, o, i) => {
            const e = expected[i];
            if (e === 0) return a;
            return a + (o - e) ** 2 / e;
        }, 0);
        const df = observed.length - 1;
        return smartRound(1 - chiSqCDF(chiSq, df));
    });

    registry.register('CHITEST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('CHISQ.TEST')!(...args);
    });

    // -----------------------------------------------------------------------
    // F distribution
    // -----------------------------------------------------------------------

    registry.register('F.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const d1 = toNum(args[1]);
        const d2 = toNum(args[2]);
        const cumulative = args[3];
        if (x < 0 || d1 < 1 || d2 < 1) throw new FormulaError(FormulaErrorType.NUM, 'F.DIST: x >= 0, d1 >= 1, d2 >= 1 required');
        if (cumulative === false || cumulative === 0) {
            // PDF
            const lnum = (d1 / 2) * Math.log(d1) + (d2 / 2) * Math.log(d2) +
                (d1 / 2 - 1) * Math.log(x) - ((d1 + d2) / 2) * Math.log(d1 * x + d2);
            const lden = gammaLn(d1 / 2) + gammaLn(d2 / 2) - gammaLn((d1 + d2) / 2);
            return smartRound(Math.exp(lnum - lden));
        }
        return smartRound(fCDF(x, d1, d2));
    });

    registry.register('F.DIST.RT', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const d1 = toNum(args[1]);
        const d2 = toNum(args[2]);
        if (x < 0 || d1 < 1 || d2 < 1) throw new FormulaError(FormulaErrorType.NUM, 'F.DIST.RT: x >= 0, d1 >= 1, d2 >= 1 required');
        return smartRound(1 - fCDF(x, d1, d2));
    });

    registry.register('FDIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const d1 = toNum(args[1]);
        const d2 = toNum(args[2]);
        if (x < 0 || d1 < 1 || d2 < 1) throw new FormulaError(FormulaErrorType.NUM, 'FDIST: x >= 0, d1 >= 1, d2 >= 1 required');
        return smartRound(1 - fCDF(x, d1, d2));
    });

    registry.register('F.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const d1 = toNum(args[1]);
        const d2 = toNum(args[2]);
        if (p <= 0 || p >= 1 || d1 < 1 || d2 < 1) throw new FormulaError(FormulaErrorType.NUM, 'F.INV: 0 < p < 1, d1 >= 1, d2 >= 1 required');
        return smartRound(fInvCDF(p, d1, d2));
    });

    registry.register('F.INV.RT', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const d1 = toNum(args[1]);
        const d2 = toNum(args[2]);
        if (p <= 0 || p >= 1 || d1 < 1 || d2 < 1) throw new FormulaError(FormulaErrorType.NUM, 'F.INV.RT: 0 < p < 1, d1 >= 1, d2 >= 1 required');
        return smartRound(fInvCDF(1 - p, d1, d2));
    });

    registry.register('FINV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const d1 = toNum(args[1]);
        const d2 = toNum(args[2]);
        if (p <= 0 || p >= 1 || d1 < 1 || d2 < 1) throw new FormulaError(FormulaErrorType.NUM, 'FINV: 0 < p < 1, d1 >= 1, d2 >= 1 required');
        return smartRound(fInvCDF(1 - p, d1, d2));
    });

    registry.register('F.TEST', (...args: FormulaValue[]) => {
        const arr1 = flattenArgs([args[0]]);
        const arr2 = flattenArgs([args[1]]);
        if (arr1.length < 2 || arr2.length < 2) throw new FormulaError(FormulaErrorType.NA, 'F.TEST: arrays must have at least 2 elements');
        const v1 = sampleVariance(arr1);
        const v2 = sampleVariance(arr2);
        if (v2 === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'F.TEST: zero variance in second array');
        const f = v1 / v2;
        const df1 = arr1.length - 1;
        const df2 = arr2.length - 1;
        const p = fCDF(f, df1, df2);
        return smartRound(2 * Math.min(p, 1 - p));
    });

    registry.register('FTEST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('F.TEST')!(...args);
    });

    // -----------------------------------------------------------------------
    // Gamma distribution
    // -----------------------------------------------------------------------

    registry.register('GAMMA', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        if (x <= 0 && Number.isInteger(x)) throw new FormulaError(FormulaErrorType.NUM, 'GAMMA: not defined for non-positive integers');
        return smartRound(gammaFn(x));
    });

    registry.register('GAMMALN', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        if (x <= 0) throw new FormulaError(FormulaErrorType.NUM, 'GAMMALN: x must be > 0');
        return smartRound(gammaLn(x));
    });

    registry.register('GAMMALN.PRECISE', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        if (x <= 0) throw new FormulaError(FormulaErrorType.NUM, 'GAMMALN.PRECISE: x must be > 0');
        return smartRound(gammaLn(x));
    });

    registry.register('GAMMA.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const alpha = toNum(args[1]);
        const beta = toNum(args[2]);
        const cumulative = args[3];
        if (x < 0 || alpha <= 0 || beta <= 0) throw new FormulaError(FormulaErrorType.NUM, 'GAMMA.DIST: x >= 0, alpha > 0, beta > 0 required');
        if (cumulative === false || cumulative === 0) {
            return smartRound(gammaPDF(x, alpha, beta));
        }
        return smartRound(gammaCDF(x, alpha, beta));
    });

    registry.register('GAMMADIST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('GAMMA.DIST')!(...args);
    });

    registry.register('GAMMA.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const alpha = toNum(args[1]);
        const beta = toNum(args[2]);
        if (p < 0 || p >= 1 || alpha <= 0 || beta <= 0) throw new FormulaError(FormulaErrorType.NUM, 'GAMMA.INV: 0 <= p < 1, alpha > 0, beta > 0 required');
        return smartRound(gammaInvCDF(p, alpha, beta));
    });

    registry.register('GAMMAINV', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('GAMMA.INV')!(...args);
    });

    // -----------------------------------------------------------------------
    // Beta distribution
    // -----------------------------------------------------------------------

    registry.register('BETA.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const alpha = toNum(args[1]);
        const beta = toNum(args[2]);
        const cumulative = args[3];
        const a = args[4] !== undefined ? toNum(args[4]) : 0;
        const b = args[5] !== undefined ? toNum(args[5]) : 1;
        if (alpha <= 0 || beta <= 0) throw new FormulaError(FormulaErrorType.NUM, 'BETA.DIST: alpha > 0 and beta > 0 required');
        if (b <= a) throw new FormulaError(FormulaErrorType.NUM, 'BETA.DIST: B must be > A');
        const xNorm = (x - a) / (b - a);
        if (xNorm < 0 || xNorm > 1) throw new FormulaError(FormulaErrorType.NUM, 'BETA.DIST: x must be between A and B');
        if (cumulative === false || cumulative === 0) {
            return smartRound(betaPDF(xNorm, alpha, beta) / (b - a));
        }
        return smartRound(betaCDF(xNorm, alpha, beta));
    });

    registry.register('BETADIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const alpha = toNum(args[1]);
        const beta = toNum(args[2]);
        const a = args[3] !== undefined ? toNum(args[3]) : 0;
        const b = args[4] !== undefined ? toNum(args[4]) : 1;
        if (alpha <= 0 || beta <= 0) throw new FormulaError(FormulaErrorType.NUM, 'BETADIST: alpha > 0 and beta > 0 required');
        if (b <= a) throw new FormulaError(FormulaErrorType.NUM, 'BETADIST: B must be > A');
        const xNorm = (x - a) / (b - a);
        if (xNorm < 0 || xNorm > 1) throw new FormulaError(FormulaErrorType.NUM, 'BETADIST: x must be between A and B');
        return smartRound(betaCDF(xNorm, alpha, beta));
    });

    registry.register('BETA.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const alpha = toNum(args[1]);
        const beta = toNum(args[2]);
        const a = args[3] !== undefined ? toNum(args[3]) : 0;
        const b = args[4] !== undefined ? toNum(args[4]) : 1;
        if (p < 0 || p > 1 || alpha <= 0 || beta <= 0) throw new FormulaError(FormulaErrorType.NUM, 'BETA.INV: 0 <= p <= 1, alpha > 0, beta > 0 required');
        if (b <= a) throw new FormulaError(FormulaErrorType.NUM, 'BETA.INV: B must be > A');
        const xNorm = betaIncInv(p, alpha, beta);
        return smartRound(a + xNorm * (b - a));
    });

    registry.register('BETAINV', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('BETA.INV')!(...args);
    });

    // -----------------------------------------------------------------------
    // Binomial distribution
    // -----------------------------------------------------------------------

    registry.register('BINOM.DIST', (...args: FormulaValue[]) => {
        const numS = Math.round(toNum(args[0]));
        const trials = Math.round(toNum(args[1]));
        const p = toNum(args[2]);
        const cumulative = args[3];
        if (numS < 0 || trials < 0 || p < 0 || p > 1 || numS > trials) {
            throw new FormulaError(FormulaErrorType.NUM, 'BINOM.DIST: invalid arguments');
        }
        if (cumulative === false || cumulative === 0) {
            return smartRound(binomCoef(trials, numS) * Math.pow(p, numS) * Math.pow(1 - p, trials - numS));
        }
        return smartRound(binomCDF(numS, trials, p));
    });

    registry.register('BINOMDIST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('BINOM.DIST')!(...args);
    });

    registry.register('BINOM.INV', (...args: FormulaValue[]) => {
        const trials = Math.round(toNum(args[0]));
        const p = toNum(args[1]);
        const alpha = toNum(args[2]);
        if (trials < 0 || p < 0 || p > 1 || alpha < 0 || alpha > 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'BINOM.INV: invalid arguments');
        }
        // Find smallest k such that BINOM.DIST(k, trials, p, true) >= alpha
        for (let k = 0; k <= trials; k++) {
            if (binomCDF(k, trials, p) >= alpha) return k;
        }
        return trials;
    });

    registry.register('CRITBINOM', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('BINOM.INV')!(...args);
    });

    // -----------------------------------------------------------------------
    // Negative binomial distribution
    // -----------------------------------------------------------------------

    registry.register('NEGBINOM.DIST', (...args: FormulaValue[]) => {
        const numF = Math.round(toNum(args[0]));
        const numS = Math.round(toNum(args[1]));
        const p = toNum(args[2]);
        const cumulative = args[3];
        if (numF < 0 || numS < 1 || p < 0 || p > 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'NEGBINOM.DIST: invalid arguments');
        }
        if (cumulative === false || cumulative === 0) {
            return smartRound(binomCoef(numF + numS - 1, numF) * Math.pow(p, numS) * Math.pow(1 - p, numF));
        }
        return smartRound(negBinomCDF(numF, numS, p));
    });

    registry.register('NEGBINOMDIST', (...args: FormulaValue[]) => {
        const numF = Math.round(toNum(args[0]));
        const numS = Math.round(toNum(args[1]));
        const p = toNum(args[2]);
        if (numF < 0 || numS < 1 || p < 0 || p > 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'NEGBINOMDIST: invalid arguments');
        }
        return smartRound(binomCoef(numF + numS - 1, numF) * Math.pow(p, numS) * Math.pow(1 - p, numF));
    });

    // -----------------------------------------------------------------------
    // Poisson distribution
    // -----------------------------------------------------------------------

    registry.register('POISSON.DIST', (...args: FormulaValue[]) => {
        const x = Math.round(toNum(args[0]));
        const lambda = toNum(args[1]);
        const cumulative = args[2];
        if (x < 0 || lambda < 0) throw new FormulaError(FormulaErrorType.NUM, 'POISSON.DIST: x >= 0 and lambda >= 0 required');
        if (cumulative === false || cumulative === 0) {
            return smartRound(Math.exp(-lambda + x * Math.log(lambda) - gammaLn(x + 1)));
        }
        return smartRound(poissonCDF(x, lambda));
    });

    registry.register('POISSON', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('POISSON.DIST')!(...args);
    });

    registry.register('POISSONDIST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('POISSON.DIST')!(...args);
    });

    // -----------------------------------------------------------------------
    // Exponential distribution
    // -----------------------------------------------------------------------

    registry.register('EXPON.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const lambda = toNum(args[1]);
        const cumulative = args[2];
        if (x < 0 || lambda <= 0) throw new FormulaError(FormulaErrorType.NUM, 'EXPON.DIST: x >= 0 and lambda > 0 required');
        if (cumulative === false || cumulative === 0) {
            return smartRound(lambda * Math.exp(-lambda * x));
        }
        return smartRound(exponCDF(x, lambda));
    });

    registry.register('EXPONDIST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('EXPON.DIST')!(...args);
    });

    // -----------------------------------------------------------------------
    // Log-normal distribution
    // -----------------------------------------------------------------------

    registry.register('LOGNORM.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        const cumulative = args[3];
        if (x <= 0 || sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'LOGNORM.DIST: x > 0 and sigma > 0 required');
        if (cumulative === false || cumulative === 0) {
            const z = (Math.log(x) - mean) / sd;
            return smartRound(Math.exp(-0.5 * z * z) / (x * sd * Math.sqrt(2 * Math.PI)));
        }
        return smartRound(lognormCDF(x, mean, sd));
    });

    registry.register('LOGNORMDIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        if (x <= 0 || sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'LOGNORMDIST: x > 0 and sigma > 0 required');
        return smartRound(lognormCDF(x, mean, sd));
    });

    registry.register('LOGNORM.INV', (...args: FormulaValue[]) => {
        const p = toNum(args[0]);
        const mean = toNum(args[1]);
        const sd = toNum(args[2]);
        if (p <= 0 || p >= 1 || sd <= 0) throw new FormulaError(FormulaErrorType.NUM, 'LOGNORM.INV: 0 < p < 1 and sigma > 0 required');
        return smartRound(lognormInvCDF(p, mean, sd));
    });

    registry.register('LOGINV', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('LOGNORM.INV')!(...args);
    });

    // -----------------------------------------------------------------------
    // Weibull distribution
    // -----------------------------------------------------------------------

    registry.register('WEIBULL.DIST', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const alpha = toNum(args[1]);
        const beta = toNum(args[2]);
        const cumulative = args[3];
        if (x < 0 || alpha <= 0 || beta <= 0) throw new FormulaError(FormulaErrorType.NUM, 'WEIBULL.DIST: x >= 0, alpha > 0, beta > 0 required');
        if (cumulative === false || cumulative === 0) {
            return smartRound(weibullPDF(x, beta, alpha));
        }
        return smartRound(weibullCDF(x, beta, alpha));
    });

    registry.register('WEIBULL', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('WEIBULL.DIST')!(...args);
    });

    registry.register('WEIBULLDIST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('WEIBULL.DIST')!(...args);
    });

    // -----------------------------------------------------------------------
    // Hypergeometric distribution
    // -----------------------------------------------------------------------

    registry.register('HYPGEOM.DIST', (...args: FormulaValue[]) => {
        const sampleS = Math.round(toNum(args[0]));
        const numberSample = Math.round(toNum(args[1]));
        const populationS = Math.round(toNum(args[2]));
        const numberPop = Math.round(toNum(args[3]));
        const cumulative = args[4];
        if (sampleS < 0 || numberSample <= 0 || populationS <= 0 || numberPop <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'HYPGEOM.DIST: invalid arguments');
        }
        if (cumulative === false || cumulative === 0) {
            return smartRound(hypergeomPMF(sampleS, numberPop, populationS, numberSample));
        }
        return smartRound(hypergeomCDF(sampleS, numberPop, populationS, numberSample));
    });

    registry.register('HYPGEOMDIST', (...args: FormulaValue[]) => {
        const sampleS = Math.round(toNum(args[0]));
        const numberSample = Math.round(toNum(args[1]));
        const populationS = Math.round(toNum(args[2]));
        const numberPop = Math.round(toNum(args[3]));
        if (sampleS < 0 || numberSample <= 0 || populationS <= 0 || numberPop <= 0) {
            throw new FormulaError(FormulaErrorType.NUM, 'HYPGEOMDIST: invalid arguments');
        }
        return smartRound(hypergeomPMF(sampleS, numberPop, populationS, numberSample));
    });

    // -----------------------------------------------------------------------
    // Fisher transformation
    // -----------------------------------------------------------------------

    registry.register('FISHER', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        if (x <= -1 || x >= 1) throw new FormulaError(FormulaErrorType.NUM, 'FISHER: x must be between -1 and 1 (exclusive)');
        return smartRound(0.5 * Math.log((1 + x) / (1 - x)));
    });

    registry.register('FISHERINV', (...args: FormulaValue[]) => {
        const y = toNum(args[0]);
        const e2y = Math.exp(2 * y);
        return smartRound((e2y - 1) / (e2y + 1));
    });

    // -----------------------------------------------------------------------
    // Confidence intervals
    // -----------------------------------------------------------------------

    registry.register('CONFIDENCE', (...args: FormulaValue[]) => {
        const alpha = toNum(args[0]);
        const sd = toNum(args[1]);
        const n = toNum(args[2]);
        if (alpha <= 0 || alpha >= 1 || sd <= 0 || n < 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'CONFIDENCE: invalid arguments');
        }
        const z = Math.abs(normalInvCDF(alpha / 2));
        return smartRound(z * sd / Math.sqrt(n));
    });

    registry.register('CONFIDENCE.NORM', (...args: FormulaValue[]) => {
        const alpha = toNum(args[0]);
        const sd = toNum(args[1]);
        const n = toNum(args[2]);
        if (alpha <= 0 || alpha >= 1 || sd <= 0 || n < 1) {
            throw new FormulaError(FormulaErrorType.NUM, 'CONFIDENCE.NORM: invalid arguments');
        }
        const z = Math.abs(normalInvCDF(alpha / 2));
        return smartRound(z * sd / Math.sqrt(n));
    });

    registry.register('CONFIDENCE.T', (...args: FormulaValue[]) => {
        const alpha = toNum(args[0]);
        const sd = toNum(args[1]);
        const n = toNum(args[2]);
        if (alpha <= 0 || alpha >= 1 || sd <= 0 || n < 2) {
            throw new FormulaError(FormulaErrorType.NUM, 'CONFIDENCE.T: invalid arguments');
        }
        const t = Math.abs(tInvCDF(alpha / 2, n - 1));
        return smartRound(t * sd / Math.sqrt(n));
    });

    // -----------------------------------------------------------------------
    // Z.TEST / ZTEST
    // -----------------------------------------------------------------------

    registry.register('Z.TEST', (...args: FormulaValue[]) => {
        const arr = flattenArgs([args[0]]);
        const x = toNum(args[1]);
        const sigma = args[2] !== undefined ? toNum(args[2]) : undefined;
        if (arr.length < 1) throw new FormulaError(FormulaErrorType.NA, 'Z.TEST: array must have at least one element');
        const n = arr.length;
        const mean = arr.reduce((a, b) => a + b, 0) / n;
        const sd = sigma !== undefined && sigma > 0 ? sigma : Math.sqrt(sampleVariance(arr));
        if (sd === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'Z.TEST: zero standard deviation');
        const z = (mean - x) / (sd / Math.sqrt(n));
        return smartRound(1 - normalCDF(z));
    });

    registry.register('ZTEST', (...args: FormulaValue[]) => {
        return (registry as unknown as { resolve: (n: string) => ((...a: FormulaValue[]) => FormulaValue) }).resolve('Z.TEST')!(...args);
    });

    // -----------------------------------------------------------------------
    // Bessel functions
    // -----------------------------------------------------------------------

    registry.register('BESSELI', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const n = Math.round(toNum(args[1]));
        if (n < 0) throw new FormulaError(FormulaErrorType.NUM, 'BESSELI: n must be >= 0');
        return smartRound(besselIn(n, x));
    });

    registry.register('BESSELJ', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const n = Math.round(toNum(args[1]));
        if (n < 0) throw new FormulaError(FormulaErrorType.NUM, 'BESSELJ: n must be >= 0');
        return smartRound(besselJn(n, x));
    });

    registry.register('BESSELK', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const n = Math.round(toNum(args[1]));
        if (n < 0 || x <= 0) throw new FormulaError(FormulaErrorType.NUM, 'BESSELK: n must be >= 0 and x > 0');
        return smartRound(besselKn(n, x));
    });

    registry.register('BESSELY', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        const n = Math.round(toNum(args[1]));
        if (n < 0 || x <= 0) throw new FormulaError(FormulaErrorType.NUM, 'BESSELY: n must be >= 0 and x > 0');
        return smartRound(besselYn(n, x));
    });
}
